import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { getLaboklinApi } from '../../services/laboklinApi';
import type { StructuredWorkup, WorkupStage } from '../../types/laboklin';

/**
 * Drives the ordinary Laboklin workup and hands back its result as panels.
 *
 * This is the same machinery the chat drawer uses and nothing about it is
 * reimplemented here: the same conversation metadata, so the assistant gets the
 * Laboklin prompt and the laboratory tools; the same opening message; the same
 * diagnostic case, processed by the same engine; the same terminal turn. What
 * differs is only that nobody renders the transcript. Each time an answer lands,
 * the structuring endpoint is asked to re-read the thread, and the page redraws
 * from that.
 *
 * The interim stage messages are status lines that are shown and replaced, and are
 * not written to the thread at all, so progress has to be read live off the socket
 * rather than fetched.
 */

export type WorkupPhase =
  | 'connecting'
  | 'interpreting'
  | 'working_up'
  /**
   * The engine has ranked and the assistant is checking that ranking against the
   * report. There is a differential on screen throughout, and it is not the answer
   * yet - the exclusion turn can still take the top of it away.
   */
  | 'reviewing'
  | 'ready'
  | 'error';

const PHASE_BY_STAGE: Record<WorkupStage, WorkupPhase> = {
  interpreting: 'interpreting',
  ranking: 'working_up',
  reviewing: 'reviewing',
  complete: 'ready',
};

/**
 * What the assistant is doing, said in the panel's voice rather than the tool's.
 *
 * These are the only signal during the minute or two the exclusion turn spends
 * reading incidence lists. Without them the panel shows a differential and a
 * spinner and nothing that says why the spinner is there.
 */
const PROGRESS_BY_TOOL: Record<string, string> = {
  get_disease_info: 'Checking the incidence of each candidate…',
  get_lab_report: 'Re-reading the report…',
  update_case: 'Recording what it set aside…',
  suggest_lab_tests: 'Looking up what would settle it…',
};

/**
 * The message MyLab sends when the veterinarian asks for an interpretation.
 *
 * Deliberately identical to MyLabPage's, including the request for differentials:
 * that phrasing is what the diagnostic-case tool requires before it will work a
 * patient up. The clinical signs are not restated - the report carries them and
 * get_lab_report returns them, so a second copy here could only drift from the
 * laboratory's own wording.
 */
function openingMessage(reportNo: string): string {
  return (
    `Interpret Laboklin report ${reportNo}. Give me the differential diagnoses for ` +
    'this patient and tell me what to do next.'
  );
}

interface SocketEvent {
  type?: string;
  conversationId?: string;
  caseId?: string;
  newStatus?: string;
  message?: string;
  text?: string;
  thought?: string;
  isThought?: boolean;
  toolName?: string;
}

export interface LabWorkupState {
  workup: StructuredWorkup | null;
  phase: WorkupPhase;
  /** What the assistant is doing right now, when it has said. */
  progress: string | null;
  error: string | null;
}

export function useLabWorkup(reportNo: string | undefined): LabWorkupState {
  const { connectToConversation, sendMessage, onMessage, isConnected } = useWebSocket();

  // Generated once per report: the socket manager tears down and reconnects when the
  // conversation id changes, so a per-render id would loop.
  const [conversationId] = useState(() => crypto.randomUUID());
  const [workup, setWorkup] = useState<StructuredWorkup | null>(null);
  const [phase, setPhase] = useState<WorkupPhase>('connecting');
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const caseIdRef = useRef<string | undefined>(undefined);
  const sentRef = useRef(false);
  // Answers can land while a previous re-read is still in flight. Only the newest
  // one may write, or a stale response overwrites a fuller page.
  const requestSeqRef = useRef(0);
  // What the page already holds. Sent back so the server can answer "nothing new"
  // from two reads instead of restructuring the whole workup.
  const signatureRef = useRef<string | undefined>(undefined);
  /**
   * Whether a re-read is already running.
   *
   * A structuring call can take longer than the poll interval, and without this
   * every response was superseded by the next request before it could be applied:
   * the sequence guard below discarded all of them, the stored signature never
   * advanced, and the page re-fetched the same workup forever without ever
   * rendering it. One at a time.
   */
  const inFlightRef = useRef(false);
  /** Whether the answer is mid-stream, so its progress line is set once, not per chunk. */
  const streamingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!reportNo || inFlightRef.current) return;
    const seq = ++requestSeqRef.current;
    inFlightRef.current = true;

    try {
      const next = await getLaboklinApi().structureWorkup({
        conversationId,
        reportNo,
        ...(caseIdRef.current ? { caseId: caseIdRef.current } : {}),
        ...(signatureRef.current ? { signature: signatureRef.current } : {}),
      });
      if (seq !== requestSeqRef.current) return;

      if (next.unchanged) return;
      signatureRef.current = next.signature;

      setWorkup(next);
      // `complete` is the fallback for a page loaded against a server that predates
      // the stage. It is the coarser of the two - it cannot distinguish "the engine
      // has ranked" from "the review has finished" - so it is only reached when
      // there is nothing better.
      setPhase(next.stage ? PHASE_BY_STAGE[next.stage] : next.complete ? 'ready' : 'working_up');
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      // A failed re-read is not a failed workup: the conversation is still running
      // and the next answer will ask again. Only report it when there is nothing on
      // screen at all.
      const message = err instanceof Error ? err.message : String(err);
      setWorkup((current) => {
        if (!current) {
          setError(message);
          setPhase('error');
        }
        return current;
      });
    } finally {
      inFlightRef.current = false;
    }
  }, [conversationId, reportNo]);

  useEffect(() => {
    if (!reportNo) return;
    connectToConversation(conversationId, {
      partner: 'laboklin',
      labReportNo: reportNo,
      labMode: 'post_result',
    }).catch((err) => {
      setError(err instanceof Error ? err.message : String(err));
      setPhase('error');
    });
  }, [conversationId, reportNo, connectToConversation]);

  // Ask once the socket is up, and only once.
  useEffect(() => {
    if (!reportNo || !isConnected || sentRef.current) return;
    sentRef.current = true;
    setPhase('interpreting');
    sendMessage(openingMessage(reportNo), conversationId);
  }, [reportNo, isConnected, conversationId, sendMessage]);

  /**
   * The safety net, and the reason the page is not at the mercy of one frame.
   *
   * The terminal turn is pushed once, over the socket, to the connections
   * registered against the conversation. That is a delivery, not a guarantee: it
   * was observed being sent while the tab that was waiting for it never asked
   * again, and the panel span on "working through the differential" with the
   * answer already written to the thread. The socket stays the fast path; this is
   * what makes the page converge when it does not arrive.
   *
   * Cheap because the request carries the signature of what is already held, so
   * the server answers "nothing new" without restructuring anything. Stops as soon
   * as the case is complete, and gives up well after the engine's own five-minute
   * ceiling rather than polling a dead conversation forever.
   */
  useEffect(() => {
    if (!reportNo || phase === 'ready' || phase === 'error') return;

    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt > 8 * 60 * 1000) {
        clearInterval(timer);
        return;
      }
      void refresh();
    }, 8000);

    return () => clearInterval(timer);
  }, [reportNo, phase, refresh]);

  useEffect(() => {
    const unsubscribe = onMessage((data: unknown) => {
      const event = data as SocketEvent;
      if (event.conversationId && event.conversationId !== conversationId) return;

      switch (event.type) {
        case 'case_created':
          if (event.caseId) {
            caseIdRef.current = event.caseId;
            setPhase('working_up');
          }
          break;

        case 'progress':
        case 'in_progress':
        case 'acknowledgment':
          setProgress(event.message ?? event.text ?? null);
          break;

        case 'thought_update':
          setProgress(event.thought ?? null);
          break;

        case 'tool_call':
          setProgress(
            (event.toolName && PROGRESS_BY_TOOL[event.toolName]) ??
              'Checking the ranking against the report…'
          );
          break;

        case 'stream_chunk':
          // Said once, and the chunk text is deliberately thrown away. Rendering
          // half-written clinical prose beside the differential would put an
          // unfinished claim on the page - the same failure this whole state exists
          // to prevent, in a new costume. All that is wanted here is the fact that
          // the answer has started arriving.
          if (!streamingRef.current) {
            streamingRef.current = true;
            setProgress('Writing the reviewed differential…');
          }
          break;

        case 'chat_response':
          // A thought is the assistant narrating itself mid-turn, not an answer.
          if (event.isThought) break;
          streamingRef.current = false;
          setProgress(null);
          void refresh();
          break;

        case 'error':
          setProgress(null);
          break;

        default:
          break;
      }
    });

    return unsubscribe;
  }, [conversationId, onMessage, refresh]);

  return { workup, phase, progress, error };
}
